import { MonoTypeOperatorFunction, retry, throwError, timer } from 'rxjs';

/** First wait before re-sending a transient failure; doubles on each further attempt. */
export const RETRY_BASE_DELAY_MS = 250;

/**
 * Retries a failed request only when retrying could plausibly succeed.
 *
 * A bare `retry(n)` retries everything, including a 4xx. Most of those are deterministic: the same
 * request rejected for the same reason will be rejected again, so the retries only delay the
 * message the user needs. On a non-idempotent POST they also re-send a write that the server may
 * have partly processed before rejecting it. The capacity save leans on this — a 4xx there is the
 * API applying a rule, the entry window among them.
 *
 * Retried: no response at all, 5xx, plus the two 4xx that are about timing rather than the request
 * itself — 408 Request Timeout and 429 Too Many Requests.
 * Not retried: every other 4xx, including the validation rejections the capacity save relies on.
 *
 * @param count Maximum retry attempts after the initial one
 * @param baseDelayMs First wait; each further attempt doubles it
 */
export function retryTransient<T>(
  count = 2,
  baseDelayMs = RETRY_BASE_DELAY_MS,
): MonoTypeOperatorFunction<T> {
  return retry<T>({
    count,
    // Returning an observable schedules the retry; throwing here ends the stream with the original
    // error and no further attempts. That is how RxJS lets a retry opt out per error.
    delay: (error: unknown, retryCount: number) =>
      isTransientFailure(error)
        ? timer(baseDelayMs * 2 ** (retryCount - 1))
        : throwError(() => error),
  });
}

/**
 * Whether a failure could plausibly succeed on another attempt.
 *
 * A missing or non-numeric status counts as transient: `HttpErrorResponse` reports `status: 0` for
 * a network failure or a blocked CORS preflight, and anything without a recognisable status is more
 * likely a transport problem than a rejection.
 */
function isTransientFailure(error: unknown): boolean {
  const status = (error as { status?: unknown })?.status;
  if (typeof status !== 'number' || status === 0) {
    return true;
  }
  return status >= 500 || status === 408 || status === 429;
}
